import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { DriverApi } from "../driver-api.ts";
import {
  COUNTDOWN_TICK_MS,
  countdownFace,
  emptyInbox,
  screenFor,
  withOffer,
  type InboxScreen,
  type InboxState,
} from "../offer-inbox.ts";
import { runOfferStream } from "../offer-stream-client.ts";
import type { NextAction, StreamClosure } from "../sse.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { HeroCountdown, HeroDisc, HeroRadar } from "./Hero.tsx";
import { PhoneShell, type ActionProps } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * The offer inbox: `2 Online`, `3 Offer` and `3b Offer expired`, over one server-sent-event stream.
 *
 * The design's own note is the specification for the transition between them: _"3: a 15-second
 * countdown ticks live; under 5 s the ring turns amber and pulses. Try it: it expires into 3b on its
 * own."_ So nothing here is pressed to move between the three — the stream and the clock decide, and
 * the address bar follows.
 *
 * ## The countdown
 *
 * `OfferEvent.expiresAt` is the server instant the offer stops being acceptable, and EXP-003's own
 * contract says it is "never a remaining duration, because a duration computed here is already wrong
 * by the time it arrives". R-10 recorded that exact defect on the dispatch console's offer board.
 * So the remaining time is computed on this phone's clock, four times a second, against that
 * instant, and the window it is a fraction of is the offer's own `expiresAt - createdAt` rather than
 * the design's sample fifteen seconds.
 *
 * ## What happens when the stream ends
 *
 * `offer-stream-client.ts` holds the contract. What this component adds is what a courier is told:
 * a reconnect is silent, because the design draws no treatment for one and a courier does not need
 * to know; a stream that stopped for good is a screen, because waiting for an offer on a connection
 * that has ended is the one thing this application must never look like it is doing.
 *
 * ## `discovery: false`
 *
 * A courier's own token cannot reach DSP-02's listing, so this connection follows the offers it was
 * told about and cannot find one by itself. EXP-003 publishes that in `stream-open` so an
 * application can say so. The design has no words for it — Q-49 — so the sub-line says it and is
 * flagged as this build's.
 */

export interface OfferInboxProps {
  readonly api: DriverApi;
  readonly baseUrl: string;
  readonly tenantId: string;
  readonly driverId: string | undefined;
  readonly accessToken: () => Promise<string | undefined>;
  /**
   * The offers this application was told about, which is the only way it can name one.
   *
   * Empty on a courier's own token means a connection EXP-003 refuses 400 — it has nothing it could
   * ever watch. That refusal is drawn rather than hidden, because an empty radar over a refused
   * connection is a courier waiting for work that will never arrive. R-22.
   */
  readonly offers: readonly string[];
  readonly asked: InboxScreen;
  readonly onScreenChange: (screen: InboxScreen) => void;
  readonly now: () => number;
  readonly fetch?: typeof globalThis.fetch;
}

export function OfferInbox(props: OfferInboxProps): ReactNode {
  const [inbox, setInbox] = useState<InboxState>(emptyInbox);
  const [stopped, setStopped] = useState<
    { readonly closure?: StreamClosure; readonly action: NextAction } | undefined
  >(undefined);
  const [refused, setRefused] = useState<{ status: number; body: string } | undefined>(undefined);
  const [commandState, setCommandState] = useState<ViewState | undefined>(undefined);
  /** Redrawn on a timer while an offer is live, so the ring and the number move. */
  const [tick, setTick] = useState(0);

  const { api, baseUrl, tenantId, driverId, accessToken, offers } = props;

  useEffect(() => {
    if (driverId === undefined) return;
    const controller = new AbortController();
    setRefused(undefined);
    setStopped(undefined);
    void runOfferStream({
      baseUrl,
      tenantId,
      driverId,
      offers,
      accessToken,
      signal: controller.signal,
      ...(props.fetch === undefined ? {} : { fetch: props.fetch }),
      handlers: {
        onOpen: (open) => setInbox((held) => ({ ...held, open, ended: false })),
        onOffer: (offer) => setInbox((held) => withOffer(held, offer)),
        onClosed: ({ closure, action }) => {
          /*
           * A reconnect is silent and a stop is a screen.
           *
           * The design draws no treatment for a reconnect, and it should not: a courier whose stream
           * reached its ten-minute ceiling has had nothing happen to them. A stop is different —
           * nothing will arrive on this connection again — and the one thing this screen must not do
           * is keep drawing a radar over it.
           */
          if (action.kind === "stop") {
            setStopped({ ...(closure === undefined ? {} : { closure }), action });
            setInbox((held) => ({ ...held, ended: true }));
          }
        },
        onRefused: setRefused,
      },
    });
    return (): void => controller.abort();
    // `offers` is an array and is compared by identity, which is correct here: a new list is a new
    // watch set and does need a new connection. The caller holds it stable.
  }, [baseUrl, tenantId, driverId, accessToken, offers, props.fetch]);

  const screen = screenFor(inbox, props.now());

  /*
   * The ticker, which runs only while there is a live offer.
   *
   * Not a `setInterval` left running for the life of the screen: a timer is a subscription that
   * outlives the thing it was for, and a radar with no offer on it has nothing to redraw. It is also
   * the mechanism by which screen 3 becomes screen 3b with nobody pressing anything — the tick that
   * crosses `expiresAt` is the one that changes what `screenFor` answers.
   */
  useEffect(() => {
    if (screen !== "offer") return;
    const timer = setInterval(() => setTick((value) => value + 1), COUNTDOWN_TICK_MS);
    return (): void => clearInterval(timer);
  }, [screen, inbox.offer?.offerId]);
  void tick;

  useEffect(() => {
    if (screen !== props.asked) props.onScreenChange(screen);
  }, [screen, props.asked, props.onScreenChange]);

  /*
   * `POST /v1/ride-offers/{offerId}/view`, once per offer.
   *
   * DSP-02 publishes it and the design implies it: the offer is on screen, so it has been viewed.
   * The ref is what makes it once rather than once per tick — `view` is idempotent and carries a key,
   * so a repeat is answered from the first rather than counted, but four requests a second over a
   * mobile connection is a courier's data spent on nothing.
   */
  const viewed = useRef<string | undefined>(undefined);
  useEffect(() => {
    const offer = inbox.offer;
    if (offer === null || screen !== "offer") return;
    if (viewed.current === offer.offerId) return;
    viewed.current = offer.offerId;
    api
      .markOfferViewed(offer.offerId, `view-${offer.offerId}`)
      // Silently: whether DSP-02 recorded the view changes nothing a courier can see or act on, and
      // a failure here must not take the offer off the screen.
      .catch(() => undefined);
  }, [api, inbox.offer, screen]);

  const accept = useCallback(() => {
    const offer = inbox.offer;
    if (offer === null) return;
    setCommandState({ kind: "loading" });
    api
      /*
       * One key per offer, not per press.
       *
       * The contract marks accept idempotent, so the key is what makes a second press — or a retry
       * over a connection that dropped after the request left — the same accept rather than a second
       * one. A fresh key per press would make a double-tap two commands, which on an offer is two
       * attempts at a job that can only be taken once.
       */
      .acceptOffer(offer.offerId, `accept-${offer.offerId}`)
      .then(() => setCommandState(undefined))
      .catch((error: unknown) => {
        /*
         * An accept that arrives after expiry is refused, and the design says so: _"an accept that
         * arrives after expiry is refused and says so. The offer went to someone else and the
         * driver stays online"_. DSP-02 refuses any transition but `expire` once `expiresAt` has
         * passed, so that refusal is a conflict and the screen the design draws for it is 3b.
         *
         * A 403 is the other refusal and it is the recorded one: R-10 and R-22 both say a courier
         * may currently never accept their own offer, because DSP-02 compares
         * `candidateRef.driverId` against the token's subject and nothing states the two are the
         * same value. That is reported with DSP-02's own code rather than drawn as an expiry,
         * because sending a courier to wait for the next offer when the real problem is an
         * identifier mismatch would hide the defect for ever.
         */
        setCommandState(stateForError(error, ["ride-offer:respond"]));
      });
  }, [api, inbox.offer]);

  const decline = useCallback(() => {
    const offer = inbox.offer;
    if (offer === null) return;
    setCommandState({ kind: "loading" });
    api
      .declineOffer(offer.offerId)
      .then(() => setCommandState(undefined))
      .catch((error: unknown) => setCommandState(stateForError(error, ["ride-offer:respond"])));
  }, [api, inbox.offer]);

  if (driverId === undefined) {
    return (
      <StateScreen
        screen="offers"
        chip={{ class: "unknown", label: "no courier id" }}
        title="We don't know which courier you are"
        sub="Your sign-in carries no courier identifier, so there is no offer stream to open. Whoever administers your account sets that."
      />
    );
  }

  if (refused !== undefined) {
    /*
     * EXP-003 refused the connection before writing a byte.
     *
     * 400 is the one worth wording separately: it means this connection had nothing it could ever
     * watch — no offer named, no cursor to replay, and a token that cannot discover one. That is the
     * discovery gap arriving as a status code, and telling a courier to try again would be telling
     * them to do the thing that cannot work.
     */
    return (
      <StateScreen
        screen="offers"
        chip={{ class: "unknown", label: "no stream" }}
        title={refused.status === 400 ? "There's nothing to watch yet" : "The offer stream refused"}
        sub={
          refused.status === 400
            ? "This connection can only follow offers it was told about, and it wasn't told about any. You'll be notified when one arrives."
            : `EXP-003 answered ${String(refused.status)}. Your sign-in may be missing driver-app:stream.`
        }
      />
    );
  }

  if (commandState !== undefined) {
    return (
      <StateScreen
        screen="offers"
        state={commandState}
        onRetry={() => setCommandState(undefined)}
        purpose="Answering the offer."
      />
    );
  }

  if (stopped !== undefined) {
    const because =
      stopped.action.kind === "stop" ? stopped.action.because : stopped.closure?.reason;
    return (
      <StateScreen
        screen="offers"
        chip={{ class: "closed", label: "stream ended" }}
        title={stoppedTitle(because)}
        sub={stoppedSub(because)}
      />
    );
  }

  if (screen === "offer-expired") return expiredScreen();
  if (screen === "offer") return offerScreen();
  return searchingScreen();

  /* ------------------------------------------------------------------------ 2 Online */

  function searchingScreen(): ReactNode {
    const open = inbox.open;
    /*
     * `Masaki zone · offers come to you automatically` — the design's sub-line.
     *
     * The second half is true of this connection and is kept word for word. The first half is a zone
     * name, and nothing published names a courier's zone: GEO-06 names an area and its availability
     * query needs a `productRef` no driver record publishes. So the zone is dropped rather than
     * invented, and the line is the half that holds.
     *
     * `discovery: false` is the other thing this screen has to say and the design gives it no words.
     * An application that waits silently on a connection that cannot find an offer is an application
     * showing a courier a radar over nothing. Q-49.
     */
    const sub =
      open !== null && !open.discovery
        ? "Offers reach you when you're told about one — this connection can't find them by itself."
        : "Offers come to you automatically";

    return (
      <PhoneShell
        screen="offers"
        art={<HeroRadar />}
        online
        earningsLabel={null}
        chip={{ class: "done", label: "online · available" }}
        title="Looking for jobs near you"
        sub={sub}
        rows={[
          /*
           * `Today · 3 jobs · TZS 7,200` and `Online for · 1 h 12 min`, drawn as the two absences
           * they are.
           *
           * Nothing publishes `earnings.totalMinor`, `summary.earningsMinor` or `earnings.netMinor`,
           * and nothing publishes `availability.onlineSince` or `availability.presenceSince`. Both
           * keys are the design's and stay; both values say which kind of nothing this is rather
           * than reading as a figure of zero.
           */
          {
            key: "Today",
            value: null,
            because: "No per-courier earnings read is published",
          },
          {
            key: "Online for",
            value: null,
            because: "Nothing publishes when this online period began",
          },
        ]}
        actions={[]}
        {...(open !== null && !open.discovery ? { flag: "wording not in the design" } : {})}
      />
    );
  }

  /* ------------------------------------------------------------------------- 3 Offer */

  function offerScreen(): ReactNode {
    const offer = inbox.offer!;
    const face = countdownFace(offer, props.now());
    const actions: ActionProps[] = [
      { label: "Slide to accept", glyph: "check", primary: true, slide: true, onPress: accept },
      { label: "Decline", glyph: "close", primary: false, onPress: decline },
    ];
    return (
      <PhoneShell
        screen="offers"
        art={<HeroCountdown face={face} />}
        online
        earningsLabel={null}
        chip={{ class: "human", label: "new offer" }}
        /*
         * `Pickup 1.2 km · Masaki` and `Drop-off Oyster Bay · 3.1 km · small parcel`.
         *
         * Neither has a source. Nothing publishes `offer.pickup.addressLabel`, `offers.pickup` or
         * `offer.pickupAddressLabel`; the event carries MOB-03's `rideRequestId`, and a courier's
         * token cannot read that request. So the headline is what the platform does publish about
         * this offer, which is the offer itself, and the two addresses are named as missing in the
         * row below rather than guessed from a distance nobody measured.
         */
        title="A job for you"
        sub={`Offer ${offer.offerId.slice(0, 8)} · rank ${String(offer.rank)}`}
        rows={[
          { key: "You earn", value: null, because: "Nothing publishes what accepting pays" },
          { key: "Pickup", value: null, because: "Nothing publishes the pickup as an address" },
          { key: "Drop-off", value: null, because: "Nothing publishes the drop-off as an address" },
        ]}
        actions={actions}
        flag="wording not in the design"
      />
    );
  }

  /* -------------------------------------------------------------------- 3b Offer expired */

  function expiredScreen(): ReactNode {
    const offer = inbox.offer;
    const cancelled = offer?.status === "cancelled";
    return (
      <PhoneShell
        screen="offers"
        art={<HeroDisc glyph="timer_off" caption="Offer expired" />}
        online
        earningsLabel={null}
        chip={{ class: "closed", label: cancelled ? "cancelled" : "expired" }}
        title={cancelled ? "That offer was withdrawn" : "That offer has gone"}
        sub={
          cancelled
            ? "It was withdrawn before you answered. Nothing is lost; you stay online."
            : "It expired before your accept reached us, so it went to another courier. Nothing is lost; you stay online."
        }
        note={{
          kind: "info",
          text: "Accepting after the timer never appears to work and then fails later.",
        }}
        actions={[
          {
            label: "Back to jobs",
            glyph: "radar",
            primary: true,
            onPress: (): void => {
              // Clear the offer, which is what puts `screenFor` back on screen 2. Nothing is sent:
              // the offer is already settled and the stream is already watching for the next one.
              setInbox((held) => ({ ...held, offer: null }));
              viewed.current = undefined;
            },
          },
        ]}
        {...(cancelled ? { flag: "wording not in the design" } : {})}
      />
    );
  }
}

function stoppedTitle(because: string | undefined): string {
  switch (because) {
    case "nothing_left_to_watch":
      return "Nothing left to watch";
    case "authorization_revoked":
      return "Your access changed";
    case "token_not_renewable":
      return "Your sign-in ran out";
    default:
      return "The offer stream ended";
  }
}

function stoppedSub(because: string | undefined): string {
  switch (because) {
    case "nothing_left_to_watch":
      /*
       * Every offer this connection was following has settled, and it cannot find another.
       *
       * Reconnecting would be refused 400, so this is not a retry problem and must not be worded as
       * one. R-22: the fix is a `driverId` filter on DSP-02's listing, or RTM-001.
       */
      return "Every offer you were following has settled, and this connection can't find a new one by itself. You'll be notified when one arrives.";
    case "authorization_revoked":
      return "Your sign-in no longer carries what the offer stream needs. Signing in again won't change that on its own — whoever administers your account does.";
    case "token_not_renewable":
      return "Your sign-in expired and could not be renewed. Sign in again to start receiving offers.";
    default:
      return "Nothing is lost. Reopen this screen to start listening again.";
  }
}
